/**
 * Landing-page translation source — English + Norwegian.
 *
 * Public surface only. Does NOT translate the MVP, /verify, /nfc, or any
 * backend artefact. Routing model: `/` = English, `/no` = Norwegian.
 *
 * Shape is mirrored 1:1 between languages so the components can read the
 * same key paths regardless of active locale.
 */

export const SUPPORTED_LANGS = ['en', 'no'];

export const COPY = {
  en: {
    nav: {
      explore: 'Explore',
      howItWorks: 'How it works',
      mvpAccess: 'MVP Access',
      enterMvp: 'Enter MVP',
    },
    hero: {
      eyebrow: 'Real insights. Real impact.',
      headline1: 'Claims are no longer enough.',
      headline2: 'Seafood needs proof.',
      sub: 'PureSustain connects batch events, cold-chain data, compliance evidence, and QR verification into one trusted product record.',
      ctaPrimary: 'Explore the platform',
      ctaSecondary: 'MVP Access',
    },
    heroCards: {
      'batch-proof': {
        title: 'Batch proof',
        subtitle: 'Product journey captured from source to handoff',
        items: ['Origin recorded', 'Processing event logged', 'Handoff documented', 'QR proof ready'],
      },
      'cold-chain': {
        title: 'Cold-chain evidence',
        subtitle: 'Sensor readings connected to product history',
        items: ['Temperature window', 'Time-stamped readings', 'Threshold alerts', 'Quality context'],
      },
      compliance: {
        title: 'Compliance-ready record',
        subtitle: 'Evidence structured for buyers, auditors, and partners',
        items: ['Traceability events', 'Sensor evidence', 'Documentation links', 'Verification access'],
      },
      'qr-verify': {
        title: 'QR verification',
        subtitle: 'Product proof made accessible to the right stakeholders',
        items: ['Scan product', 'Review batch story', 'Verify key evidence', 'Share trusted record'],
      },
    },
    trust: 'PureSustain turns fragmented seafood supply-chain data into product proof buyers, auditors, and partners can trust.',
    problem: {
      eyebrow: 'The market has shifted',
      title: 'Why proof is the new standard',
      cards: [
        { title: 'Claims alone are no longer enough', body: 'Sustainability and provenance statements now require evidence, not assertions.' },
        { title: 'Auditors and buyers expect proof',   body: 'Compliance reviews and procurement teams ask for verifiable, end-to-end records.' },
        { title: 'Consumers want evidence they can understand', body: 'A QR scan should reveal a clear, trustworthy story \u2014 not a black box.' },
      ],
    },
    proof: {
      eyebrow: 'What PureSustain proves',
      title: 'One trusted product record \u2014 from origin to shelf',
      tiles: [
        { title: 'Live batch capture',    body: 'Operator-verified events recorded as each batch moves through the chain.' },
        { title: 'Cold-chain evidence',   body: 'Temperature telemetry tied directly to the batch it belongs to.' },
        { title: 'Audit-ready records',   body: 'A verified chain of custody, exportable for compliance reviews.' },
        { title: 'QR verification',       body: 'A consumer-facing surface that turns the record into product proof.' },
      ],
    },
    how: {
      eyebrow: 'How it works',
      title: 'From data to proof \u2014 in three steps',
      steps: [
        { title: 'Capture', body: 'Batch events, sensor telemetry, and compliance entries are recorded in real time.' },
        { title: 'Verify',  body: 'Each event is bound to its batch and added to a verified chain of custody.' },
        { title: 'Share',   body: 'The product record becomes scannable proof for retailers, auditors, and consumers.' },
      ],
    },
    audience: {
      eyebrow: 'Built for the chain',
      title: 'A shared product record for every stakeholder in the chain',
      cards: ['Seafood producers', 'Processors and exporters', 'Technology partners', 'Auditors and buyers'],
    },
    mvp: {
      heading: 'MVP access is reserved for authorized visitors.',
      body: 'The MVP is available to selected pilot partners, investors, and invited stakeholders. It is hosted under the same PureSustain domain and provides a controlled view of the platform\u2019s current capabilities.',
      enter: 'Enter MVP',
      request: 'Request Pilot Access',
    },
    pilot: {
      eyebrow: 'Pilot Access',
      title: 'Interested in piloting PureSustain?',
      body: 'PureSustain is engaging with seafood industry partners who want to improve traceability, cold-chain visibility, compliance readiness, and trusted product communication.',
      support: 'Ideal for seafood producers, processors, technology partners, and supply-chain teams exploring verified traceability.',
      cta: 'Request Pilot Access',
    },
    team: {
      eyebrow: 'People',
      title: 'Founder-led. Technically supported. Built for real supply chains.',
      members: [
        {
          id: 'rim-ghebremariam',
          name: 'Rim Ghebremariam',
          role: 'Founder & CEO',
          photo: '/team-rim.jpg',
          bio: 'Supply-chain systems consultant with experience across enterprise integrations, business processes, and digital transformation. Rim built the PureSustain MVP to turn fragmented seafood supply-chain data into verified product proof.',
          focusLabel: 'Focus:',
          focus: 'Supply-chain workflows, traceability, MVP development, partnerships.',
        },
        {
          id: 'alina-lapina',
          name: 'Alina Lapina',
          role: 'Advisory CTO',
          photo: '/team-alina.jpg',
          bio: 'Technical advisor supporting PureSustain\u2019s architecture direction across secure systems, blockchain, compliance-oriented technology, and scalable platform development.',
          focusLabel: 'Focus:',
          focus: 'Secure architecture, blockchain, compliance systems, technical strategy.',
        },
      ],
    },
    contact: {
      eyebrow: 'Contact',
      title: 'Let\u2019s make seafood trust visible.',
      body: 'For pilot projects, strategic partnerships, investor conversations, or MVP access, contact PureSustain.',
      form: {
        labelName: 'Name',
        labelCompany: 'Company',
        labelEmail: 'Email',
        labelRole: 'Role',
        labelInterest: 'Interest area',
        labelMessage: 'Message',
        placeholderName: 'Jane Doe',
        placeholderCompany: 'Company name',
        placeholderEmail: 'jane@company.com',
        placeholderRole: 'Your role',
        placeholderMessage: 'Tell us briefly what you have in mind.',
        interestOptions: [
          { value: 'Pilot',       label: 'Pilot' },
          { value: 'Partnership', label: 'Partnership' },
          { value: 'Investment',  label: 'Investment' },
          { value: 'MVP access',  label: 'MVP access' },
          { value: 'Other',       label: 'Other' },
        ],
        submit: 'Send request',
        sending: 'Sending\u2026',
        defaultError: 'Could not send your request. Please try again.',
        successTitle: 'Request received',
        successBody: 'Thank you. The PureSustain team will follow up shortly.',
      },
    },
    footer: {
      tagline: 'Real insights. Real impact.',
      description: 'A trusted product record for seafood supply chains.',
      contactLabel: 'Contact',
      platformLabel: 'Platform',
      requestPilot: 'Request Pilot Access',
      linkExplore: 'Explore',
      linkHow: 'How it works',
      linkMvp: 'MVP Access',
      linkEnter: 'Enter MVP \u2192',
      copyright: '\u00a9 2026 PureSustain AS. All rights reserved.',
    },
    lang: {
      enLabel: 'View page in English',
      noLabel: 'Vis siden p\u00e5 norsk',
      groupLabel: 'Language',
    },
    a11y: {
      navHome: 'PureSustain \u2014 home',
      logoAlt: 'PureSustain \u2014 Real insights. Real impact.',
    },
  },

  no: {
    nav: {
      explore: 'Utforsk',
      howItWorks: 'Slik fungerer det',
      mvpAccess: 'MVP-tilgang',
      enterMvp: 'G\u00e5 til MVP',
    },
    hero: {
      eyebrow: 'Reelle innsikter. Reell effekt.',
      headline1: 'P\u00e5stander er ikke lenger nok.',
      headline2: 'Sj\u00f8mat trenger bevis.',
      sub: 'PureSustain samler batchhendelser, kj\u00f8lekjededata, compliance-bevis og QR-verifisering i \u00e9n p\u00e5litelig produktoversikt.',
      ctaPrimary: 'Utforsk plattformen',
      ctaSecondary: 'MVP-tilgang',
    },
    heroCards: {
      'batch-proof': {
        title: 'Batch-bevis',
        subtitle: 'Produktets reise fra opprinnelse til levering, dokumentert',
        items: ['Opprinnelse registrert', 'Prosessering loggf\u00f8rt', 'Overlevering dokumentert', 'QR-bevis klart'],
      },
      'cold-chain': {
        title: 'Kj\u00f8lekjede-bevis',
        subtitle: 'Sensoravlesninger koblet til produkthistorikken',
        items: ['Temperaturvindu', 'Tidsstemplede avlesninger', 'Grenseverdivarsler', 'Kvalitetskontekst'],
      },
      compliance: {
        title: 'Compliance-klar dokumentasjon',
        subtitle: 'Bevis strukturert for kj\u00f8pere, revisorer og partnere',
        items: ['Sporbarhetshendelser', 'Sensorbevis', 'Dokumentasjonslenker', 'Verifiseringstilgang'],
      },
      'qr-verify': {
        title: 'QR-verifisering',
        subtitle: 'Produktbevis gjort tilgjengelig for de rette interessentene',
        items: ['Skann produkt', 'Se batchens historie', 'Verifiser sentrale bevis', 'Del p\u00e5litelig dokumentasjon'],
      },
    },
    trust: 'PureSustain gj\u00f8r fragmenterte data fra sj\u00f8matens forsyningskjede til produktbevis som kj\u00f8pere, revisorer og partnere kan stole p\u00e5.',
    problem: {
      eyebrow: 'Markedet har endret seg',
      title: 'Hvorfor bevis er den nye standarden',
      cards: [
        { title: 'P\u00e5stander alene holder ikke lenger', body: 'B\u00e6rekraft- og opprinnelseserkl\u00e6ringer krever n\u00e5 bevis, ikke bare p\u00e5stander.' },
        { title: 'Revisorer og kj\u00f8pere forventer bevis', body: 'Compliance-revisjoner og innkj\u00f8psteam etterspr verifiserbar dokumentasjon gjennom hele kjeden.' },
        { title: 'Forbrukere vil ha bevis de kan forst\u00e5', body: 'En QR-skanning skal vise en tydelig og troverdig historie \u2014 ikke en sort boks.' },
      ],
    },
    proof: {
      eyebrow: 'Hva PureSustain dokumenterer',
      title: '\u00c9n p\u00e5litelig produktoversikt \u2014 fra opprinnelse til butikk',
      tiles: [
        { title: 'Sanntids batchregistrering', body: 'Operat\u00f8rverifiserte hendelser registreres mens hver batch beveger seg gjennom kjeden.' },
        { title: 'Kj\u00f8lekjede-bevis',        body: 'Temperaturtelemetri knyttet direkte til batchen den tilh\u00f8rer.' },
        { title: 'Revisjonsklar dokumentasjon', body: 'En verifisert sporingskjede, eksporterbar for compliance-revisjoner.' },
        { title: 'QR-verifisering',             body: 'En forbrukervennlig flate som gj\u00f8r dokumentasjonen til produktbevis.' },
      ],
    },
    how: {
      eyebrow: 'Slik fungerer det',
      title: 'Fra data til bevis \u2014 i tre steg',
      steps: [
        { title: 'Registrer', body: 'Batchhendelser, sensortelemetri og compliance-oppf\u00f8ringer registreres i sanntid.' },
        { title: 'Verifiser', body: 'Hver hendelse knyttes til sin batch og legges til i en verifisert sporingskjede.' },
        { title: 'Del',       body: 'Produktoversikten blir skannbart bevis for butikker, revisorer og forbrukere.' },
      ],
    },
    audience: {
      eyebrow: 'Bygget for kjeden',
      title: '\u00c9n felles produktoversikt for alle akt\u00f8rer i kjeden',
      cards: ['Sj\u00f8matprodusenter', 'Prosess\u00f8rer og eksport\u00f8rer', 'Teknologipartnere', 'Revisorer og kj\u00f8pere'],
    },
    mvp: {
      heading: 'MVP-tilgang er forbeholdt autoriserte bes\u00f8kende.',
      body: 'MVP-en er tilgjengelig for utvalgte pilotpartnere, investorer og inviterte interessenter. Den ligger under samme PureSustain-domene og gir et kontrollert innblikk i plattformens n\u00e5v\u00e6rende funksjonalitet.',
      enter: 'G\u00e5 til MVP',
      request: 'Be om pilottilgang',
    },
    pilot: {
      eyebrow: 'Pilottilgang',
      title: 'Vurderer dere \u00e5 pilotere PureSustain?',
      body: 'PureSustain samarbeider med akt\u00f8rer i sj\u00f8matn\u00e6ringen som vil styrke sporbarhet, kj\u00f8lekjedeinnsikt, compliance-beredskap og troverdig produktkommunikasjon.',
      support: 'Passer for sj\u00f8matprodusenter, prosess\u00f8rer, teknologipartnere og forsyningskjedeteam som utforsker verifisert sporbarhet.',
      cta: 'Be om pilottilgang',
    },
    team: {
      eyebrow: 'Mennesker',
      title: 'Grunnleggerdrevet. Teknisk forankret. Bygget for reelle forsyningskjeder.',
      members: [
        {
          id: 'rim-ghebremariam',
          name: 'Rim Ghebremariam',
          role: 'Grunnlegger og daglig leder',
          photo: '/team-rim.jpg',
          bio: 'Konsulent innen forsyningskjedesystemer med erfaring fra virksomhetsintegrasjoner, forretningsprosesser og digital transformasjon. Rim utviklet PureSustain-MVP-en for \u00e5 gj\u00f8re fragmenterte data fra sj\u00f8matens forsyningskjede til verifisert produktbevis.',
          focusLabel: 'Fokus:',
          focus: 'Forsyningskjede-arbeidsflyter, sporbarhet, MVP-utvikling, partnerskap.',
        },
        {
          id: 'alina-lapina',
          name: 'Alina Lapina',
          role: 'R\u00e5dgivende CTO',
          photo: '/team-alina.jpg',
          bio: 'Teknisk r\u00e5dgiver som st\u00f8tter PureSustains arkitekturretning innen sikre systemer, blokkjede, compliance-orientert teknologi og skalerbar plattformutvikling.',
          focusLabel: 'Fokus:',
          focus: 'Sikker arkitektur, blokkjede, compliance-systemer, teknisk strategi.',
        },
      ],
    },
    contact: {
      eyebrow: 'Kontakt',
      title: 'La oss gj\u00f8re tillit til sj\u00f8mat synlig.',
      body: 'For pilotprosjekter, strategiske partnerskap, investorsamtaler eller MVP-tilgang \u2014 ta kontakt med PureSustain.',
      form: {
        labelName: 'Navn',
        labelCompany: 'Selskap',
        labelEmail: 'E-post',
        labelRole: 'Rolle',
        labelInterest: 'Interesseomr\u00e5de',
        labelMessage: 'Melding',
        placeholderName: 'Ola Nordmann',
        placeholderCompany: 'Selskapsnavn',
        placeholderEmail: 'ola@selskap.no',
        placeholderRole: 'Din rolle',
        placeholderMessage: 'Fortell oss kort hva du tenker.',
        interestOptions: [
          { value: 'Pilot',       label: 'Pilot' },
          { value: 'Partnership', label: 'Partnerskap' },
          { value: 'Investment',  label: 'Investering' },
          { value: 'MVP access',  label: 'MVP-tilgang' },
          { value: 'Other',       label: 'Annet' },
        ],
        submit: 'Send foresp\u00f8rsel',
        sending: 'Sender\u2026',
        defaultError: 'Foresp\u00f8rselen kunne ikke sendes. Pr\u00f8v igjen.',
        successTitle: 'Foresp\u00f8rsel mottatt',
        successBody: 'Takk! PureSustain-teamet tar kontakt s\u00e5 snart som mulig.',
      },
    },
    footer: {
      tagline: 'Reelle innsikter. Reell effekt.',
      description: 'Et p\u00e5litelig produktregister for sj\u00f8matens forsyningskjeder.',
      contactLabel: 'Kontakt',
      platformLabel: 'Plattform',
      requestPilot: 'Be om pilottilgang',
      linkExplore: 'Utforsk',
      linkHow: 'Slik fungerer det',
      linkMvp: 'MVP-tilgang',
      linkEnter: 'G\u00e5 til MVP \u2192',
      copyright: '\u00a9 2026 PureSustain AS. Med enerett.',
    },
    lang: {
      enLabel: 'View page in English',
      noLabel: 'Vis siden p\u00e5 norsk',
      groupLabel: 'Spr\u00e5k',
    },
    a11y: {
      navHome: 'PureSustain \u2014 hjem',
      logoAlt: 'PureSustain \u2014 Reelle innsikter. Reell effekt.',
    },
  },
};
